import React from 'react'
import { Teacher } from '../../providers/context'
import { TextInput } from '../../components/form/textInput'
import { makeRequest, showAxiosErrorAsToast } from '../../request'
import {
  IconEmail,
  IconGlobe,
  IconLogins,
  IconPhone,
  IconReceivedArrow,
  IconSentArrow,
  IconStar,
  IconUnpause,
} from '../../icons/library'
import { capitalizeWords, formatPhoneNumber } from '../../utils'
import { Avatar, AvatarInfo } from '../../components/avatar'
import { Tooltip } from '../../components/simple/tooltip'
import { Badge, BadgeStyle } from '../../components/simple/badge'
import { LanguageGlobe } from '../home/messageTags'
import { Popups } from '../../providers/popups'
import { Toasts } from '../../providers/toast'
import { LabeledCheckbox } from '../../components/form/checkbox'
import { ContactEmailData, ContactPhoneData, StudentContact, UsageData } from './familyContactData'
import { Link } from 'react-router-dom'
import { useLanguages } from '../../providers/languages'
import { SmartLinkOptional } from '../../components/simple/link'

export const GenericContact: React.FC<{
  className?: string
  avatar: AvatarInfo
  title: React.ReactNode
  children: React.ReactNode
  deleted?: boolean
  loading?: boolean
}> = ({ avatar, title, children, deleted = false, loading = false, className }) => {
  return (
    <div
      className={`${className} flex flex-col gap-3 rounded-[5px] bg-white p-3 shadow-even ${deleted ? 'opacity-50' : ''} ${loading ? 'pointer-events-none opacity-50' : ''}`}
    >
      <div className="flex items-center gap-2">
        <Avatar src={avatar} />
        {title}
      </div>
      {children}
    </div>
  )
}

export const ContactSibling: React.FC<{
  sibling: StudentContact['siblings'][0]
}> = ({ sibling }) => {
  return (
    <div className="flex flex-col gap-2 rounded-[5px] bg-white p-3 shadow-even">
      <div className="flex items-center gap-2">
        <Avatar src={sibling.avatar} />
        <div className="mr-auto flex flex-col pr-2 leading-5">
          <span className="text-[15px] font-bold">{sibling.name}</span>
          {sibling.schools.length > 0 ? (
            <Tooltip text={sibling.schools.length === 1 ? undefined : sibling.schools.join(', ')}>
              <span className="text-nowrap text-sm font-bold text-slate-600">
                {sibling.schools.length === 1 ? sibling.schools[0] : `${sibling.schools.length} schools`}
              </span>
            </Tooltip>
          ) : (
            <span className="text-sm font-bold text-red-500">No School</span>
          )}
        </div>
        <Badge badgeStyle={BadgeStyle.mint} className="ml-auto">
          {sibling.grade || 'none'}
        </Badge>
      </div>
    </div>
  )
}

export const EmailEdit: React.FC<{
  emailData: ContactEmailData
  user: Teacher
  email: string
  setEmail: React.Dispatch<React.SetStateAction<string>>
  receiveEmails: boolean
  setReceiveEmails: React.Dispatch<React.SetStateAction<boolean>>
  invalid: boolean
}> = ({ emailData, user, email, setEmail, receiveEmails, setReceiveEmails, invalid }) =>
  emailData.showEditSection && (
    <>
      <LabeledCheckbox
        checked={receiveEmails}
        onChecked={async () => {
          if (
            !emailData!.sendingManuallyDeactivated ||
            (user.admin > 0 &&
              (await Popups.simpleConfirm(
                `Are you sure you want to enable sending emails to this user? Do not do this unless they have asked you to enable this.`
              )))
          )
            setReceiveEmails(!receiveEmails)
        }}
        disabled={emailData.sendingManuallyDeactivated && user.admin === 0}
        label="Send this user emails"
        tooltip={emailData.sendingManuallyDeactivated ? 'This user has requested not to receive emails' : undefined}
        className="mr-auto"
      />
      <TextInput
        blue
        value={email}
        onChange={setEmail}
        placeholder="email@example.com"
        disabled={!emailData.canEditEmail}
        icon={<IconEmail />}
        invalid={invalid}
        validation="email"
      />
    </>
  )

export const EmailRow: React.FC<{
  contactName: string
  contactID: number
  emailData: ContactEmailData
  sendEmailMessageLink: string
  setLoading: React.Dispatch<React.SetStateAction<boolean>>
  reloadContacts: () => void
}> = ({ contactName, contactID, emailData, sendEmailMessageLink, setLoading, reloadContacts }) => (
  <div className="flex items-center gap-2">
    <IconEmail className="text-slate-300" />
    {emailData.status === 'reachable' ? (
      emailData.receiveEmails ? (
        <Link to={sendEmailMessageLink} className="no-underline hover:underline">
          {emailData.email}
        </Link>
      ) : (
        <Tooltip text={`${contactName} does not receive emails`}>
          <span className="text-slate-400">{emailData.email}</span>
        </Tooltip>
      )
    ) : emailData.status === 'bounced' ? (
      <>
        {emailData.authUserCanReactivate && (
          <ReactivateButton
            contactID={contactID}
            contactType="email"
            contact={emailData!.email}
            markedAsSpam={emailData!.markedAsSpam}
            setLoading={setLoading}
            reloadContacts={reloadContacts}
          />
        )}
        <Tooltip text={`${emailData.email} is not a reachable email`}>
          <span className="text-red-500">{emailData.email}</span>
        </Tooltip>
      </>
    ) : null}
    {emailData.pendingChange && (
      <IconStar
        className="ml-auto text-warning"
        tooltip={`This user has an unconfirmed email change to ${emailData.pendingChange}`}
      />
    )}
  </div>
)

export const PhoneEdit: React.FC<{
  phoneData: ContactPhoneData
  user: Teacher
  phoneNumber: string
  setPhoneNumber: React.Dispatch<React.SetStateAction<string>>
  receiveTexts: boolean
  setReceiveTexts: React.Dispatch<React.SetStateAction<boolean>>
  textAsWhatsApp: boolean
  setTextAsWhatsApp: React.Dispatch<React.SetStateAction<boolean>>
  showSendTextToSpeech: boolean
  sendTextToSpeech: boolean
  setSendTextToSpeech: React.Dispatch<React.SetStateAction<boolean>>
  receiveCalls: boolean
  setReceiveCalls: React.Dispatch<React.SetStateAction<boolean>>
  invalid: boolean
}> = ({
  phoneData,
  user,
  phoneNumber,
  setPhoneNumber,
  receiveTexts,
  setReceiveTexts,
  textAsWhatsApp,
  setTextAsWhatsApp,
  showSendTextToSpeech,
  sendTextToSpeech,
  setSendTextToSpeech,
  receiveCalls,
  setReceiveCalls,
  invalid,
}) => {
  return (
    phoneData.showEditSection && (
      <>
        {!textAsWhatsApp && (
          <LabeledCheckbox
            checked={receiveTexts}
            onChecked={async (c) => {
              if (
                !phoneData!.sendingManuallyDeactivated ||
                (user.admin > 0 &&
                  (await Popups.simpleConfirm(
                    `Are you sure you want to enable sending texts to this user? Do not do this unless they have asked you to enable this.`
                  )))
              )
                setReceiveTexts(c)
            }}
            disabled={phoneData.sendingManuallyDeactivated && user.admin === 0}
            label="Send this user texts"
            tooltip={phoneData.sendingManuallyDeactivated ? 'This user has requested not to receive texts' : undefined}
            className="mr-auto"
          />
        )}
        <TextInput
          blue
          value={phoneNumber}
          onChange={setPhoneNumber}
          placeholder="xxx-xxx-xxxx"
          disabled={!phoneData.canEditPhoneNumber}
          icon={<IconPhone />}
          invalid={invalid}
          validation="phone"
        />
        {receiveTexts && (
          <LabeledCheckbox
            checked={textAsWhatsApp}
            onChecked={async (c) => {
              if (
                (c &&
                  (await Popups.simpleConfirm(
                    'Are you sure you want to send text messages to this users as WhatsApp messages instead? This user will no longer recieve any sms text messages.'
                  ))) ||
                (!c && (await Popups.simpleConfirm('Are you sure you want to disable WhatsApp messages to this user?')))
              )
                setTextAsWhatsApp(c)
            }}
            label="Send text messages through WhatsApp"
          />
        )}
        <LabeledCheckbox
          checked={receiveCalls}
          onChecked={async (c) => {
            if (
              (c &&
                (await Popups.simpleConfirm(
                  'Are you sure you want to send phone calls to this user? Do not do this unless they have asked you to enable this.'
                ))) ||
              (!c &&
                (await Popups.simpleConfirm(
                  'Are you sure you want to disable phone calls to this user? Do not do this unless they have asked you to disable this.'
                )))
            )
              setReceiveCalls(c)
          }}
          label="Receives broadcast calls"
        />
        {showSendTextToSpeech && (
          <LabeledCheckbox
            checked={sendTextToSpeech}
            onChecked={setSendTextToSpeech}
            label="Send messages through text-to-speech"
          />
        )}
      </>
    )
  )
}

export const PhoneRow: React.FC<{
  contactName: string
  contactID: number
  phoneData: ContactPhoneData
  sendTextMessageLink: string
  setLoading: React.Dispatch<React.SetStateAction<boolean>>
  reloadContacts: () => void
}> = ({ contactName, contactID, phoneData, sendTextMessageLink, setLoading, reloadContacts }) => {
  const displayPhoneNumber = formatPhoneNumber(phoneData.phoneNumber, true)
  return (
    <div className="flex items-center gap-2">
      <IconPhone className="text-slate-300" />
      {phoneData.status === 'reachable' ? (
        phoneData.receiveTexts ? (
          <Tooltip text={phoneData.textAsWhatsApp ? 'Receives texts as WhatsApp' : undefined}>
            <Link
              to={sendTextMessageLink}
              className={`no-underline hover:underline ${phoneData.textAsWhatsApp ? 'text-whatsapp' : ''}`}
            >
              {displayPhoneNumber}
            </Link>
          </Tooltip>
        ) : (
          <Tooltip text={`${contactName} does not receive texts`}>
            <span className="text-slate-400">{displayPhoneNumber}</span>
          </Tooltip>
        )
      ) : phoneData.status === 'landline' ? (
        <Tooltip text={`${displayPhoneNumber}  is a landline number. We cannot text it.`}>
          <span className="text-purple-500">{displayPhoneNumber}</span>
        </Tooltip>
      ) : phoneData.status === 'bounced' ? (
        <>
          {phoneData.authUserCanReactivate && (
            <ReactivateButton
              contactID={contactID}
              contactType="phoneNumber"
              contact={phoneData!.phoneNumber}
              setLoading={setLoading}
              reloadContacts={reloadContacts}
            />
          )}
          <Tooltip text={`${displayPhoneNumber} is not a reachable phone number`}>
            <span className="text-red-500">{displayPhoneNumber}</span>
          </Tooltip>
        </>
      ) : null}
      {phoneData.pendingChange && (
        <IconStar
          className="ml-auto text-warning"
          tooltip={`This user has an unconfirmed phone number change to ${phoneData.pendingChange}`}
        />
      )}
    </div>
  )
}

export const LanguageRow: React.FC<{ language: string; spokenLanguage?: string; languageGlobe: LanguageGlobe }> = ({
  language,
  spokenLanguage,
  languageGlobe,
}) => {
  const languages = useLanguages()
  return (
    <div className="flex items-center gap-2 text-nowrap text-slate-400">
      {(language != 'en' || spokenLanguage) && <IconGlobe className="text-slate-300" tooltip={languageGlobe.tooltip} />}
      {[language, spokenLanguage]
        .map((l) => languages.find(l)?.name)
        .filter((l) => !!l)
        .join(', ')}
    </div>
  )
}

export const UsageRow: React.FC<{
  data: UsageData
  showText?: boolean
  className?: string
  deliveryStatusID?: number
}> = ({ data, showText = false, className, deliveryStatusID }) => (
  <div className={`flex flex-col ${className}`}>
    {showText && <div className="text-sm text-slate-400">Usage {capitalizeWords(data.timerange)}</div>}
    <div className="flex items-center gap-3">
      <Tooltip text={data.sentTooltip}>
        <SmartLinkOptional
          to={deliveryStatusID ? `/admin/deliveryStatus/${deliveryStatusID}/incoming` : undefined}
          className="flex items-center gap-1 font-bold text-sky-500 no-underline"
        >
          <IconSentArrow />
          {data.sent}
        </SmartLinkOptional>
      </Tooltip>
      <Tooltip text={data.receivedTooltip}>
        <SmartLinkOptional
          to={deliveryStatusID ? `/admin/deliveryStatus/${deliveryStatusID}` : undefined}
          className="flex items-center gap-1 font-bold text-blue-500 no-underline"
        >
          <IconReceivedArrow />
          {data.received}
        </SmartLinkOptional>
      </Tooltip>
      {data.logins !== undefined && (
        <Tooltip text={data.loginsTooltip}>
          <div className="flex items-center gap-1 font-bold text-warning-500">
            <IconLogins />
            {data.logins}
          </div>
        </Tooltip>
      )}
    </div>
  </div>
)

export const ReactivateButton: React.FC<{
  contactID: number
  contactType: 'email' | 'phoneNumber'
  contact: string
  markedAsSpam?: boolean
  setLoading: React.Dispatch<React.SetStateAction<boolean>>
  reloadContacts: () => void
}> = ({ contactID, contactType, contact, markedAsSpam = false, setLoading, reloadContacts }) => {
  return (
    <Tooltip text="This contact is not reachable - click to unpause messages to this user">
      <div
        className={`cursor-pointer ${markedAsSpam ? 'text-red-500' : 'text-charcoal-text'}`}
        onClick={async () => {
          setLoading(true)
          await reactivatePausedContact(
            {
              userID: contactID,
              contactType,
              contact,
            },
            reloadContacts,
            markedAsSpam
          )
          setLoading(false)
        }}
      >
        <IconUnpause />
        {markedAsSpam && <span className="ml-1 text-sm">SPAM</span>}
      </div>
    </Tooltip>
  )
}

const reactivatePausedContact = async (reactivateData: any, onComplete: () => void, spam: boolean = false) => {
  const confirmed =
    !spam ||
    (await Popups.confirm({
      title: (
        <b className="text-lg">
          This user marked us as spam and must manually remove us from their spam filter to continue receiving messages.
        </b>
      ),
      message: 'Only reactivate here after that has been done.',
      yesText: 'Continue',
      cancel: 'Cancel',
    }))
  if (confirmed) {
    try {
      const response = await makeRequest<string>('/reactivateContact', reactivateData)
      Toasts.showToast({
        message: response.replace('phoneNumber', 'phone number'),
      })
      onComplete()
    } catch (e) {
      showAxiosErrorAsToast(e)
    }
  }
}
